import { useEffect, useState } from "react";
import { CloseIcon, EditIcon, PlayIcon, PauseIcon } from "./icons";
import AccountPanel from "./AccountPanel";
import type { AuthUser } from "../types/auth";

interface NewArrivalSubscription {
  id: string;
  categories: string[];
  created_at: string;
  enabled: boolean;
  max_price: number;
  min_price: number;
  models?: string[];
  name: string;
  notification_count: number;
  paused: boolean;
}

interface NotificationHistoryItem {
  created_at: string;
  error_message?: string;
  id: string;
  product_category: string;
  product_id: string;
  product_image_url: string;
  product_name: string;
  product_price: number;
  product_specs: string;
  status: "sent" | "failed";
  subscription_id: string;
}

interface NotificationModalProps {
  authUser: AuthUser | null;
  categories: string[];
  isOpen: boolean;
  onAuthChange: (user: AuthUser | null) => void;
  onClose: () => void;
}

export default function NotificationModal({
  authUser,
  categories,
  isOpen,
  onAuthChange,
  onClose,
}: NotificationModalProps) {
  const [subscriptions, setSubscriptions] = useState<NewArrivalSubscription[]>([]);
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!authUser) {
      setSubscriptions([]);
      setNotificationHistory([]);
      return;
    }

    const loadData = async () => {
      try {
        const [subscriptionsRes, historyRes] = await Promise.all([
          fetch("/api/new-arrival-subscriptions"),
          fetch("/api/notification-history?limit=20"),
        ]);

        if (subscriptionsRes.status === 401 || historyRes.status === 401) {
          onAuthChange(null);
          setSubscriptions([]);
          setNotificationHistory([]);
          return;
        }

        const subscriptionsData = await subscriptionsRes.json();
        const historyData = await historyRes.json();
        setSubscriptions(subscriptionsData.subscriptions || []);
        setNotificationHistory(historyData.data || []);
      } catch (error) {
        console.error("Failed to load notification data:", error);
      }
    };

    void loadData();
  }, [isOpen, authUser, onAuthChange]);

  useEffect(() => {
    if (!selectedCategory || !isOpen) {
      setAvailableModels([]);
      return;
    }

    const fetchFilterOptions = async () => {
      try {
        const res = await fetch(
          `/api/filter-options?category=${encodeURIComponent(selectedCategory)}`,
        );
        const data = await res.json();
        setAvailableModels(data.models || []);
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
        setAvailableModels([]);
      }
    };

    void fetchFilterOptions();
  }, [selectedCategory, isOpen]);

  const fetchSubscriptions = async () => {
    if (!authUser) {
      setSubscriptions([]);
      return;
    }

    try {
      const res = await fetch("/api/new-arrival-subscriptions");
      if (res.status === 401) {
        onAuthChange(null);
        setSubscriptions([]);
        return;
      }
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
    }
  };

  const resetForm = () => {
    setName("");
    setSelectedCategory("");
    setSelectedModels([]);
    setMinPrice("");
    setMaxPrice("");
    setEditingId(null);
  };

  const startEdit = (sub: NewArrivalSubscription) => {
    setEditingId(sub.id);
    setName(sub.name);
    setSelectedCategory(sub.categories?.[0] || "");
    setSelectedModels(sub.models || []);
    setMinPrice(sub.min_price > 0 ? String(sub.min_price) : "");
    setMaxPrice(sub.max_price > 0 ? String(sub.max_price) : "");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!authUser) {
      alert("请先登录");
      return;
    }

    if (!authUser.has_bark_key) {
      alert("请先在账号中配置 Bark Key");
      return;
    }

    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        name,
        categories: selectedCategory ? [selectedCategory] : [],
        models: selectedModels,
        enabled: true,
      };

      if (minPrice) payload.min_price = Number.parseFloat(minPrice);
      if (maxPrice) payload.max_price = Number.parseFloat(maxPrice);

      const url = editingId
        ? `/api/new-arrival-subscriptions/${editingId}`
        : "/api/new-arrival-subscriptions";
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        alert(data.error || "保存失败");
        return;
      }

      resetForm();
      await fetchSubscriptions();
    } catch (error) {
      console.error("Failed to save subscription:", error);
      alert("保存失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("确定要删除这个通知设置吗？")) return;

    try {
      await fetch(`/api/new-arrival-subscriptions/${id}`, { method: "DELETE" });
      await fetchSubscriptions();
      if (editingId === id) {
        resetForm();
      }
    } catch (error) {
      console.error("Failed to delete subscription:", error);
    }
  };

  const handlePauseResume = async (id: string, paused: boolean) => {
    try {
      const endpoint = paused ? "/pause" : "/resume";
      await fetch(`/api/new-arrival-subscriptions/${id}${endpoint}`, {
        method: "PATCH",
      });
      await fetchSubscriptions();
    } catch (error) {
      console.error("Failed to pause/resume subscription:", error);
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategory(selectedCategory === category ? "" : category);
    setSelectedModels([]);
  };

  const toggleModel = (model: string) => {
    setSelectedModels(
      selectedModels.includes(model)
        ? selectedModels.filter((item) => item !== model)
        : [...selectedModels, model],
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slideUp"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-[#1D1D1F]">
              {editingId ? "编辑通知设置" : "上新通知设置"}
            </h2>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {authUser && (
              <AccountPanel
                user={authUser}
                onAuthChange={onAuthChange}
              />
            )}

            {authUser && !authUser.has_bark_key && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                请先配置 Bark Key，否则无法接收通知
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="mb-6 p-4 bg-gray-50 rounded-2xl"
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-[#1D1D1F]">
                  {editingId ? "编辑通知" : "添加新通知"}
                </h3>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    取消编辑
                  </button>
                )}
              </div>

              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">
                  名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="例如: MacBook Pro M3 通知"
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">
                  产品类型 <span className="text-gray-400">(可选)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        selectedCategory === category
                          ? "bg-[#0071E3] text-white"
                          : "bg-white border border-gray-200 text-[#1D1D1F] hover:bg-gray-50"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {selectedCategory && availableModels.length > 0 && (
                <div className="mb-3 p-3 bg-white rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs text-gray-500">
                      产品型号 <span className="text-gray-400">(可选，多选)</span>
                    </label>
                    {selectedModels.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedModels([])}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        清除选择
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {availableModels.map((model) => (
                      <button
                        key={model}
                        type="button"
                        onClick={() => toggleModel(model)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          selectedModels.includes(model)
                            ? "bg-[#0071E3] text-white"
                            : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {model}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3 flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">
                    最低价格 (可选)
                  </label>
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(event) => setMinPrice(event.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">
                    最高价格 (可选)
                  </label>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(event) => setMaxPrice(event.target.value)}
                    placeholder="无限制"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0071E3]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !authUser || !authUser.has_bark_key}
                className="w-full py-2.5 bg-[#0071E3] text-white rounded-xl font-semibold hover:bg-[#0077ED] transition-colors disabled:opacity-50"
              >
                {loading
                  ? "保存中..."
                  : editingId
                    ? "更新通知设置"
                    : "保存通知设置"}
              </button>
            </form>

            <div>
              <h3 className="text-sm font-semibold text-[#1D1D1F] mb-3">
                已设置的通知
              </h3>
              {subscriptions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  暂无通知设置
                </p>
              ) : (
                <div className="space-y-2">
                  {subscriptions.map((sub) => (
                    <div
                      key={sub.id}
                      className={`p-3 rounded-xl ${sub.paused ? "bg-gray-100 opacity-75" : "bg-gray-50"}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-[#1D1D1F]">
                              {sub.name}
                            </span>
                            {sub.paused && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 text-orange-700">
                                已暂停
                              </span>
                            )}
                            {!sub.enabled && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-200 text-gray-500">
                                禁用
                              </span>
                            )}
                            {sub.notification_count > 0 && (
                              <span className="text-xs text-gray-400">
                                已通知 {sub.notification_count} 次
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {sub.categories.length > 0
                              ? sub.categories.join(", ")
                              : "全部分类"}
                            {sub.models && sub.models.length > 0
                              ? ` · 型号: ${sub.models.join(", ")}`
                              : ""}
                            {sub.min_price > 0 && ` · ¥${sub.min_price}+`}
                            {sub.max_price > 0 && ` · ¥${sub.max_price}-`}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handlePauseResume(sub.id, !sub.paused)}
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                            title={sub.paused ? "恢复通知" : "暂停通知"}
                          >
                            {sub.paused ? (
                              <PlayIcon className="w-4 h-4" />
                            ) : (
                              <PauseIcon className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => startEdit(sub)}
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                            title="编辑"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(sub.id)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="删除"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-[#1D1D1F] mb-3">
                通知历史
              </h3>
              {notificationHistory.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  暂无通知记录
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {notificationHistory.map((item) => (
                    <div key={item.id} className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-start gap-3">
                        {item.product_image_url && (
                          <img
                            src={item.product_image_url}
                            alt={item.product_name}
                            className="w-10 h-10 object-contain rounded-lg bg-white"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-[#1D1D1F] truncate">
                              {item.product_name}
                            </span>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                item.status === "sent"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {item.status === "sent" ? "已发送" : "失败"}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            ¥{item.product_price.toLocaleString()} · {item.product_category}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(item.created_at).toLocaleString("zh-CN")}
                          </div>
                          {item.error_message && (
                            <div className="text-xs text-red-500 mt-1 bg-red-50 p-2 rounded">
                              {item.error_message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
